<template>
  <div class="w-full bg-white rounded shadow p-4">
    <div class="flex justify-center mb-4">
      <button 
        @click="activeTab = 'payment'" 
        :class="[
          'px-6 py-2 rounded-l font-medium',
          activeTab === 'payment' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'
        ]"
      >
        Make a Payment
      </button>
      <button 
        @click="activeTab = 'claim'" 
        :class="[
          'px-6 py-2 rounded-r font-medium',
          activeTab === 'claim' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'
        ]"
      >
        File a Claim
      </button>
    </div>

    <input 
      type="text" 
      class="px-3 py-2 mb-4 w-full border border-gray-300 rounded" 
      placeholder="Search for your carrier..." 
      v-model="searchTerm"
    >

    <div class="carriers-list overflow-auto" style="max-height: 600px;">
      <div 
        v-for="carrier in filteredCarriers" 
        :key="carrier.id" 
        class="grid grid-cols-1 items-center border-b py-6 gap-4"
        :class="{
          'sm:grid-cols-4': carrier.logoPath && carrier.logoPath.trim() !== '' && !imageErrors[carrier.id],
          'sm:grid-cols-3': !carrier.logoPath || carrier.logoPath.trim() === '' || imageErrors[carrier.id]
        }"
      >
        <!-- Only show logo column if it exists and loads successfully -->
        <div 
          v-if="carrier.logoPath && carrier.logoPath.trim() !== '' && !imageErrors[carrier.id]"
          class="sm:col-span-1 flex justify-center items-center"
        >
          <img 
            :src="carrier.logoPath" 
            class="max-w-[150px] sm:max-w-[180px] max-h-[80px] object-contain mx-auto"
            :alt="carrier.name + ' Logo'"
            @error="handleImageError(carrier.id)"
          >
        </div>

        <!-- Carrier Information -->
        <div 
          :class="{
            'sm:col-span-2': carrier.logoPath && carrier.logoPath.trim() !== '' && !imageErrors[carrier.id],
            'sm:col-span-2': !carrier.logoPath || carrier.logoPath.trim() === '' || imageErrors[carrier.id]
          }"
          class="text-xs sm:text-sm py-4 text-center sm:text-left"
        >
          <p class="text-black text-lg font-bold mb-2">{{ carrier.name }}</p>
          
          <!-- Payment Tab Information -->
          <template v-if="activeTab === 'payment'">
            <p class="text-gray-700 mb-1 flex flex-col sm:flex-row sm:items-center">
              <span class="font-medium sm:w-32">Billing Phone:</span>
              <a :href="'tel:+1' + carrier.billingPhone.replace(/\D/g, '')" class="text-red-600 underline">
                {{ carrier.billingPhone }}
              </a>
            </p>
            <div class="text-gray-700 mb-1 flex flex-col sm:flex-row">
              <span class="font-medium sm:w-32 sm:mt-1">Payment Address:</span>
              <span class="text-red-600 sm:flex-1">
                {{ carrier.overnightPaymentAddress }}
              </span>
            </div>
          </template>

          <!-- Claim Tab Information -->
          <template v-else>
            <p class="text-gray-700 mb-1 flex flex-col sm:flex-row sm:items-center">
              <span class="font-medium sm:w-32">Claims Phone:</span>
              <a :href="'tel:+1' + carrier.claimsPhone.replace(/\D/g, '')" class="text-red-600 underline">
                {{ carrier.claimsPhone }}
              </a>
            </p>
            <div class="text-gray-700 mb-1 flex flex-col sm:flex-row">
              <span class="font-medium sm:w-32 sm:mt-1">Instructions:</span>
              <span class="text-red-600 sm:flex-1">
                Contact your agent or call the claims number to initiate a claim.
              </span>
            </div>
          </template>

          <p class="text-gray-700 mb-1 flex flex-col sm:flex-row sm:items-center">
            <span class="font-medium sm:w-32">Website:</span>
            <a :href="carrier.website" class="text-red-600 underline" target="_blank">
              {{ carrier.website }}
            </a>
          </p>
        </div>

        <!-- Action Button -->
        <div :class="{'sm:col-span-1': true}" class="flex justify-center items-center">
          <a 
            :href="activeTab === 'payment' ? carrier.paymentUrl : carrier.claimsUrl" 
            target="_blank" 
            :class="[
              'rounded-lg px-4 py-3 transition-colors w-full text-center block font-medium',
              (activeTab === 'payment' && carrier.paymentUrl) || (activeTab === 'claim' && carrier.claimsUrl)
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-400 text-gray-300 cursor-not-allowed'
            ]"
            :disabled="(activeTab === 'payment' && !carrier.paymentUrl) || (activeTab === 'claim' && !carrier.claimsUrl)"
          >
            {{ activeTab === 'payment' ? 'Make a Payment' : 'File a Claim' }}
          </a>
        </div>
      </div>

      <!-- No results message -->
      <div v-if="filteredCarriers.length === 0" class="text-center py-8">
        <p class="text-gray-600">No carriers found matching your search.</p>
      </div>
    </div>
  </div>
</template>

<script>
import carrierData from '../../data/carrierServices.json';

export default {
  name: 'CarrierServices',
  data() {
    return {
      carriers: carrierData,
      searchTerm: '',
      activeTab: 'payment', // 'payment' or 'claim'
      imageErrors: {}
    };
  },
  computed: {
    filteredCarriers() {
      if (!this.searchTerm) {
        return this.carriers;
      }
      
      const searchLower = this.searchTerm.toLowerCase();
      return this.carriers.filter(carrier => 
        carrier.name.toLowerCase().includes(searchLower)
      );
    }
  },
  methods: {
    handleImageError(carrierId) {
      // Hide broken images
      this.imageErrors[carrierId] = true;
    }
  }
};
</script>

<style scoped>
.carriers-list {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

.carriers-list::-webkit-scrollbar {
  width: 8px;
}

.carriers-list::-webkit-scrollbar-track {
  background: #f7fafc;
  border-radius: 4px;
}

.carriers-list::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 4px;
  border: 2px solid #f7fafc;
}
</style> 